/**
 * @file CallsListView.tsx
 * @description Dedicated view for floor staff managing guest assistance calls (Water, Bill, Waiter).
 */

import React from 'react';
import type { WaiterServiceCall } from '../../../types/waiter.types';
import { Bell, Droplets, Receipt, Sparkles, CheckCircle2, Clock } from 'lucide-react';

interface CallsListViewProps {
  calls: WaiterServiceCall[];
  isLoading: boolean;
  onResolveCall: (callId: string) => Promise<void>;
}

export const CallsListView: React.FC<CallsListViewProps> = ({
  calls,
  isLoading,
  onResolveCall,
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 bg-[#121212] p-3 rounded-2xl border border-white/10">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#ff4f00]" />
          {/* Section banner clarifying both floor assistance and bill requests */}
          <span className="text-xs font-black uppercase tracking-wider text-white">
            Guest Service & Bill Calls
          </span>
        </div>
        <span className="text-xs font-bold text-zinc-400">
          {calls.length} Active Calls
        </span>
      </div>

      {isLoading && calls.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-zinc-500 text-sm">
          Loading assistance calls...
        </div>
      ) : calls.length === 0 ? (
        <div className="h-64 flex flex-col items-center justify-center text-zinc-500 text-center">
          <Bell className="w-10 h-10 mb-2 text-zinc-600" />
          <p className="text-sm font-bold text-zinc-300">No Pending Calls</p>
          <p className="text-xs text-zinc-500 mt-0.5">
            All guest service requests from the public menu have been attended.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...calls]
            .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
            .map(call => {
              const isBill = call.type.toLowerCase() === 'bill';
              const isWater = call.type.toLowerCase() === 'water';
              const isClean = call.type.toLowerCase() === 'clean';

              const callDate = new Date(call.created_at);
              const timeStr = !isNaN(callDate.getTime())
                ? callDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : '';
              const diffMinutes = Math.max(0, Math.floor((Date.now() - callDate.getTime()) / 60000));
              const isUrgent = diffMinutes >= 5;

              return (
                <div
                  key={call.id}
                  className={`p-5 rounded-3xl bg-[#121212] border flex flex-col justify-between ${
                    isBill
                      ? 'border-rose-500/70 shadow-lg shadow-rose-950/30 ring-1 ring-rose-500/30'
                      : isWater
                      ? 'border-cyan-500/70 shadow-lg shadow-cyan-950/30'
                      : 'border-amber-500/70 shadow-lg shadow-amber-950/30'
                  }`}
                >
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <h4 className="text-xl font-black text-white">Table {call.table_number}</h4>
                        <div className="flex items-center gap-1.5 text-xs text-zinc-400 mt-1 flex-wrap">
                          <Clock className="w-3.5 h-3.5 text-zinc-500" />
                          <span>Called at {timeStr}</span>
                          <span>•</span>
                          <span className={`font-mono font-bold ${isUrgent ? 'text-rose-400 animate-pulse' : 'text-zinc-300'}`}>
                            {diffMinutes === 0 ? 'Just now' : `${diffMinutes}m ago`}
                          </span>
                          {isUrgent && (
                            <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-rose-950 text-rose-300 border border-rose-800 tracking-wider">
                              Urgent
                            </span>
                          )}
                        </div>
                      </div>

                    <div
                      className={`w-10 h-10 rounded-2xl flex items-center justify-center ${
                        isBill
                          ? 'bg-rose-500/20 text-rose-400'
                          : isWater
                          ? 'bg-cyan-500/20 text-cyan-400'
                          : isClean
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'bg-amber-500/20 text-amber-400'
                      }`}
                    >
                      {isBill ? (
                        <Receipt className="w-5 h-5 animate-bounce" />
                      ) : isWater ? (
                        <Droplets className="w-5 h-5 animate-bounce" />
                      ) : isClean ? (
                        <Sparkles className="w-5 h-5 animate-bounce" />
                      ) : (
                        <Bell className="w-5 h-5 animate-bounce" />
                      )}
                    </div>
                  </div>

                  <div className="p-3 rounded-2xl bg-black/40 border border-white/5 mb-4">
                    <p className="text-xs font-bold text-zinc-200">
                      {isBill
                        ? 'Guest requested the bill / check'
                        : isWater
                        ? 'Guest requested drinking water'
                        : isClean
                        ? 'Guest requested table cleanup'
                        : 'Guest called for waiter assistance'}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => onResolveCall(call.id)}
                  className="w-full py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-emerald-950/40 cursor-pointer transition-all"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Mark Attended</span>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
